import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@frontend-platform/ui-lib/ui';
import { RotateCcw, SlidersHorizontal } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { clearDevSession } from './dev-session';
import { DevShellBotPropertiesDialog } from './dev-shell-bot-properties';
import { resetDevState } from './dev-state';

const USER_KEY = 'user';

type StoredUser = { firstName?: unknown; lastName?: unknown; email?: unknown };

function readDevUser(): StoredUser | null {
  try {
    const parsed = JSON.parse(localStorage.getItem(USER_KEY) || 'null') as unknown;
    return typeof parsed === 'object' && parsed !== null ? (parsed as StoredUser) : null;
  } catch {
    return null;
  }
}

function asText(value: unknown) {
  return typeof value === 'string' ? value : '';
}

export interface DevShellUserMenuProps {
  projectName: string;
  /** App-specific localStorage keys that Reset dev state also clears. */
  resetKeys?: string[];
  /** Extra menu content, rendered above Reset dev state. */
  children?: ReactNode;
}

export function DevShellUserMenu({ projectName, resetKeys, children }: DevShellUserMenuProps) {
  const [confirmReset, setConfirmReset] = useState(false);
  const [editProperties, setEditProperties] = useState(false);
  const user = readDevUser();
  const firstName = asText(user?.firstName);
  const lastName = asText(user?.lastName);
  const email = asText(user?.email);
  const initials = `${firstName[0] ?? ''}${lastName[0] ?? ''}`.toUpperCase();
  const name = [firstName, lastName].filter(Boolean).join(' ');

  async function reset() {
    resetDevState(projectName, resetKeys);
    await clearDevSession();
    window.location.reload();
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          aria-label={initials ? `Dev menu for ${name}` : 'Dev menu (no dev user)'}
          className={`flex size-8 items-center justify-center rounded-full font-semibold text-sm transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/30 ${
            initials ? 'bg-steel-ultradark text-white' : 'bg-steel-blue-15 text-steel-ultralight'
          }`}
        >
          {initials || '?'}
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          <DropdownMenuLabel className="flex flex-col gap-0.5">
            <span className="truncate">{name || 'No dev user'}</span>
            <span className="truncate font-normal text-steel-light text-xs">
              {email || 'bootstrapDevAuth() seeds localStorage.user on start.'}
            </span>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => setEditProperties(true)}>
            <SlidersHorizontal size={14} aria-hidden />
            AI Agent properties…
          </DropdownMenuItem>
          {children}
          <DropdownMenuItem onSelect={() => setConfirmReset(true)}>
            <RotateCcw size={14} aria-hidden />
            Reset dev state…
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {editProperties ? (
        <DevShellBotPropertiesDialog onClose={() => setEditProperties(false)} />
      ) : null}

      <AlertDialog open={confirmReset} onOpenChange={setConfirmReset}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset dev state?</AlertDialogTitle>
            <AlertDialogDescription>
              Clears the saved access token, AI Agent, dev user, recent AI Agents, mock flags and
              dev-shell settings for this browser, and the shared session the other local apps read,
              then reloads. bootstrapDevAuth() seeds the defaults again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={reset}>Reset and reload</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
